import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchServiceDocuments, getServiceDocuments } from 'app/store/slices/services';
import { setupChangeEmailInitialValues } from 'app/utils/forms/services/change-email';
import { SERVICES_PROPERTIES } from 'app/config/services';
import { fetchNewRequest } from 'app/store/slices/requests';

export default function useChangeEmail({ cdServico }) {
    const dispatch = useDispatch();

    const [loadingDocuments, setLoadingDocuments] = useState(false);

    const newRequest = useSelector((state) => state.requests.newRequest);
    const request = useSelector((state) => state.requests.details);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const editing = useMemo(() => !!request.cdSolicitacao, [request.cdSolicitacao]);

    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    const initialValues = useMemo(() => {
        if (editing) {
            if (requestJsonData.documentos) {
                dispatch(fetchServiceDocuments({ cdServico: cdServico }));
            }
            return requestJsonData;
        }
        const _newRequest = {
            cdServico: cdServico,
            company: Object.keys(activeCompany).length > 0 ? activeCompany : {},
            user: Object.keys(registrationUserInfo).length > 0 ? registrationUserInfo : {},
        };
        return setupChangeEmailInitialValues(_newRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, editing, requestJsonData]);

    const validationSchema = useMemo(
        () => (SERVICES_PROPERTIES[cdServico] ? SERVICES_PROPERTIES[cdServico].validationSchema : {}),
        [cdServico],
    );

    const canEdit = useMemo(() => {
        if (request.situacao) return request.situacao.boEdicaoPortalExterno;
        return true;
    }, [request]);

    useEffect(() => {
        (async () => {
            if (Boolean(cdServico)) {
                setLoadingDocuments(true);
                await dispatch(fetchNewRequest(true, { cdServico }));
                if (registrationUserInfo && Object.keys(registrationUserInfo).length > 0) {
                    const search = { cdServico };
                    if (requestJsonData?.cdTipoPessoaFisica || activeCompany.cdTipoPessoaFisica) {
                        search.cdTipoPessoaFisica = parseInt(
                            requestJsonData.cdTipoPessoaFisica || activeCompany.cdTipoPessoaFisica,
                        );
                    } else {
                        const cdTipoEmpresa = requestJsonData.cdTipoEmpresa
                            ? requestJsonData.cdTipoEmpresa
                            : activeCompany.cdTipoEmpresa;
                        if (cdTipoEmpresa) {
                            search.cdTipoEmpresa = parseInt(cdTipoEmpresa);
                        }
                    }
                    await dispatch(fetchServiceDocuments(search));
                }
                setLoadingDocuments(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, cdServico]);

    useEffect(() => {
        (async () => {
            const hasNewRequest = newRequest && Object.keys(newRequest).length > 0;
            const hasNoRegistrationUserInfo = !registrationUserInfo || Object.keys(registrationUserInfo).length === 0;
            if (hasNewRequest && hasNoRegistrationUserInfo) {
                const { jsonSolicitacao } = newRequest;
                if (jsonSolicitacao && jsonSolicitacao.trim() !== '') {
                    try {
                        const parsedRequest = JSON.parse(jsonSolicitacao);
                        dispatch(getServiceDocuments(parsedRequest.documentos));
                    } catch (error) {
                        console.error('Error parsing JSON request:', error);
                    }
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, newRequest]);

    return {
        initialValues,
        editing,
        validationSchema,
        canEdit,
        loadingDocuments,
        setLoadingDocuments,
    };
}
